/*** handle all events of cropper */
// fireUploadCallBack.js (ES6 Module)
// Photo upload + crop: the crop frame is FREE (any aspect, resizable) and
// starts by framing (almost) the whole photo, so a tall personal photo can
// be taken entirely - the renderer fits it into the design's photo window
// as it always has. The toolbar gains rotate-left / rotate-right / reset
// buttons injected by JS (the generated HTML stays untouched).
export function fireUploadCallBack(settingData) {
  const imageWidth = settingData.general?.imageWidth ?? 300;
  const uploadCount = settingData.general?.uploadCount;
  const isMultiple = uploadCount !== undefined;

  let files = [];
  let isCropped = false;
  let isUploadedBefore = false;
  let cropper = null;
  let currentIndex = 0;

  // Cached DOM elements
  const fileInputs = document.querySelectorAll('.file-input');
  const resultContainers = document.querySelectorAll('.result');
  const croppedImages = document.querySelectorAll('.cropped');
  const imgResults = document.querySelectorAll('.img-result');
  const modalEl = document.getElementById('modal');
  if (!modalEl) {
    console.error("Modal element not found!");
    return;
  }
  let modal = null;

  // Stroke SVG icons for the crop toolbar (self-contained: the pages do not
  // load an icon font for these buttons, so the <i class="fa ..."> elements
  // in the generated markup render empty without injected icons).
  const svgIcon = (inner) =>
    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${inner}</svg>`;
  const ICONS = {
    zoomIn: svgIcon('<circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.5" y2="16.5"></line><line x1="11" y1="8" x2="11" y2="14"></line><line x1="8" y1="11" x2="14" y2="11"></line>'),
    zoomOut: svgIcon('<circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.5" y2="16.5"></line><line x1="8" y1="11" x2="14" y2="11"></line>'),
    rotateRight: svgIcon('<polyline points="23 4 23 10 17 10"></polyline><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>'),
    rotateLeft: svgIcon('<polyline points="1 4 1 10 7 10"></polyline><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>'),
    reset: svgIcon('<polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><polyline points="21 15 21 21 15 21"></polyline><polyline points="3 9 3 3 9 3"></polyline>')
  };

  /** Add a caption label under a toolbar button's icon. */
  const addToolLabel = (btn, text) => {
    if (!btn || btn.querySelector('.crop-tool-label')) return;
    const label = document.createElement('span');
    label.className = 'crop-tool-label';
    label.textContent = text;
    btn.appendChild(label);
    btn.title = text;
    btn.setAttribute('aria-label', text);
  };

  /** Create one toolbar button (icon + caption) in the crop modal. */
  const makeToolButton = (className, icon, text) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `btn btn-primary ${className}`;
    btn.innerHTML = icon;
    addToolLabel(btn, text);
    return btn;
  };

  // === Toolbar setup (runs once): layout class, icons + labels for the
  // existing zoom buttons, and the new rotate / reset buttons. ===
  let toolbarReady = false;
  const setupToolbar = () => {
    if (toolbarReady) return;
    const zoomInBtn = document.querySelector('.zoomin-btn');
    const zoomOutBtn = document.querySelector('.zoomout-btn');
    const toolbar = zoomInBtn?.parentElement;
    if (!toolbar) return;
    toolbarReady = true;
    toolbar.classList.add('crop-toolbar');

    // The generated buttons carry FontAwesome <i> icons that never render
    // (no icon font is loaded), so give the zoom buttons real SVG icons.
    if (zoomInBtn && !zoomInBtn.querySelector('svg')) zoomInBtn.insertAdjacentHTML('afterbegin', ICONS.zoomIn);
    if (zoomOutBtn && !zoomOutBtn.querySelector('svg')) zoomOutBtn.insertAdjacentHTML('afterbegin', ICONS.zoomOut);
    addToolLabel(zoomInBtn, 'تكبير');
    addToolLabel(zoomOutBtn, 'تصغير');

    const rotateRightBtn = makeToolButton('rotateright-btn', ICONS.rotateRight, 'تدوير يمين');
    const rotateLeftBtn = makeToolButton('rotateleft-btn', ICONS.rotateLeft, 'تدوير يسار');
    const resetBtn = makeToolButton('cropreset-btn', ICONS.reset, 'إعادة ضبط');
    toolbar.append(rotateRightBtn, rotateLeftBtn, resetBtn);

    rotateRightBtn.addEventListener('click', () => { if (cropper) cropper.rotate(90); });
    rotateLeftBtn.addEventListener('click', () => { if (cropper) cropper.rotate(-90); });
    resetBtn.addEventListener('click', () => { if (cropper) cropper.reset(); });

    // The generated title warns about empty (black) squares; the locked
    // crop frame makes those impossible, so say what to do instead.
    const titleEl = document.getElementById('modalLabel');
    if (titleEl) titleEl.textContent = 'عدّل الصورة: حرّكها ودوّرها وقرّبها حتى تملأ الإطار';
  };

  // Reset validation messages
  const resetValidation = () => {
    const count = isMultiple ? uploadCount : 1;
    for (let i = 0; i < count; i++) {
      const el = document.getElementById(`lblfieldvalidation_${i}`);
      if (el) el.textContent = '';
    }
  };

  // Form validation
  const validateInputFile = (e) => {
    const count = isMultiple ? uploadCount : 1;
    for (let i = 0; i < count; i++) {
      const input = fileInputs[i];
      if (!input || input.files.length === 0) {
        const label = document.getElementById(`lblfieldvalidation_${i}`);
        const point = document.getElementById(`hpoint_${i}`);
        const submitBtn = e.submitter;

        if (point) point.style.display = 'block';
        if (label) {
          label.textContent = 'من فضلك قم برفع صورة ';
          label.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        if (submitBtn) {
          submitBtn.textContent = 'تحميل';
          submitBtn.disabled = false;
        }

        setTimeout(() => window.scrollBy(0, -400), 100);
        e.preventDefault();
        return;
      }
    }
  };

  // === 1. File Input Change → Open Modal & Load Image ===
  fileInputs.forEach(input => {
    input.addEventListener('change', (e) => {
      const fileInput = e.target;

      currentIndex = parseInt(fileInput.id.split('_')[1] || '0', 10);

      // Validate the picked file before opening the modal or reading it:
      // photos only (jpeg/png/webp), max 10 MB - anything else would hang
      // or crash silently inside readAsDataURL / the cropper.
      const pickedFile = fileInput.files && fileInput.files[0];
      if (pickedFile) {
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        const maxFileSize = 10 * 1024 * 1024;
        const validationLabel = document.getElementById(`lblfieldvalidation_${currentIndex}`);
        const rejectFile = (message) => {
          if (validationLabel) {
            validationLabel.textContent = message;
            validationLabel.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          fileInput.value = '';
        };
        if (!allowedTypes.includes(pickedFile.type)) {
          rejectFile('من فضلك اختر صورة بصيغة JPG أو PNG أو WEBP');
          return;
        }
        if (pickedFile.size > maxFileSize) {
          rejectFile('حجم الصورة كبير جدًا، الحد الأقصى 10 ميجابايت');
          return;
        }
        if (validationLabel) validationLabel.textContent = '';
      }

      setupToolbar();
      modal = new bootstrap.Modal(modalEl, { backdrop: 'static', keyboard: false });
      modal.show();

      const result = resultContainers[0];
      if (result) {
        result.innerHTML = `
        <div class="container loader-container">
          <div class="loader"></div>
          <p class="plz-wait-index">جاري تحميل الصورة...</p>
        </div>
      `;
      }

      const hpoint = document.getElementById(`hpoint_${currentIndex}`);
      if (hpoint) {
        hpoint.style.setProperty('display', 'none');
      }
      resetValidation();

      if (!fileInput.files[0]) return;

      let reader = new FileReader();
      reader.onload = (ev) => {
        if (!ev.target || !ev.target.result) return;

        // Destroy previous cropper if exists
        if (cropper) {
          cropper.destroy();
          cropper = null;
        }
        // Use unique ID per input
        const imageId = `image_${currentIndex}`;
        let img = document.getElementById(imageId);

        // Remove old image if exists
        if (img) {
          img.remove(); // or img.parentNode.removeChild(img)
        }


        img = document.createElement('img');
        img.id = imageId;
        img.src = ev.target.result;

        img.classList.add('img-responsive');

        if (result) {
          result.innerHTML = '';
          result.appendChild(img);
        }
        cropper = new Cropper(img, {
          dragMode: 'move',
          responsive: true,
          zoomOnTouch: true,
          zoomOnWheel: true,
          touchDragZoom: true,
          zoomable: true,
          autoCropArea: 0.98,
          scalable: true,
          restore: false,
          guides: true,
          center: true,
          highlight: false,
          cropBoxMovable: true,
          cropBoxResizable: true,
          minCropBoxWidth: 40,
          minCropBoxHeight: 40,
          toggleDragModeOnDblclick: false,
          minContainerWidth: window.innerWidth < 768 ? window.innerWidth * 0.9 : 300,
          minContainerHeight: window.innerWidth < 768 ? window.innerHeight * 0.5 : 300,
          viewMode: 2,
          zoom: (event) => {
            if (event.detail.ratio < 0.1) { event.preventDefault(); cropper.zoomTo(0.1); }
            if (event.detail.ratio > 3) { event.preventDefault(); cropper.zoomTo(3); }
          },
          ready: () => {
            const container = document.querySelector('.cropper-container');


            if (container) {
              container.addEventListener('touchstart', (e) => {
                if (e.touches.length > 1) e.preventDefault();
              }, { passive: false });
            }
          }
        });
      };

      reader.readAsDataURL(fileInput.files[0]);
    });
  });

  // === 2. Form Submit Validation ===
  document.querySelectorAll('.container-form').forEach(form => {
    form.addEventListener('submit', validateInputFile);
  });

  // === 3. Modal Hidden → Cleanup ===
  modalEl.addEventListener('hidden.bs.modal', () => {
    if (!isUploadedBefore && !isMultiple) {
      fileInputs.forEach(input => input.value = '');
    }
    resultContainers.forEach(el => el.innerHTML = '');
  });

  // === 4. Agree Button → Crop & Save ===
  document.querySelectorAll('.agree').forEach(btn => {
    btn.addEventListener('click', () => {
      btn.textContent = 'من فضلك أنتظر';
      btn.disabled = true;
      document.querySelectorAll('.close-btn').forEach(b => b.disabled = true);

      cropAndSave(cropper, imageWidth);
    });
  });

  // === 5. Close Button → Destroy Cropper & Hide Modal ===
  document.querySelectorAll('.close-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      if (cropper) {
        cropper.destroy();
        cropper = null;
      }
      modal?.hide();
    });
  });

  // === 6. Zoom buttons ===
  document.querySelectorAll('.zoomin-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      if (cropper) {
        cropper.zoom(0.1)
      }
    });
  });
  document.querySelectorAll('.zoomout-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      if (cropper) {
        const containerData = cropper.getContainerData();
        const imageData = cropper.getImageData();
        if (imageData.width > containerData.width) {
          cropper.zoom(-0.1);
        }
      }
    });
  });

  // === 7. Move buttons (kept wired; the toolbar CSS hides them because
  // dragging the image directly replaces them on touch screens) ===
  document.querySelectorAll('.moveleft-btn').forEach(btn => btn.addEventListener('click', () => {
    if (cropper) cropper.move(-10, 0)
  }));
  document.querySelectorAll('.moveright-btn').forEach(btn => btn.addEventListener('click', () => {
    if (cropper) cropper.move(10, 0)
  }));
  document.querySelectorAll('.moveup-btn').forEach(btn => btn.addEventListener('click', () => {
    if (cropper) cropper.move(0, -10)
  }));
  document.querySelectorAll('.movedown-btn').forEach(btn => btn.addEventListener('click', () => {
    if (cropper) cropper.move(0, 10)
  }));

  // === Inner Function: Crop and Save ===
  function cropAndSave(cropperInstance, width = 300) {
    if (!cropperInstance) {
      document.querySelectorAll('.agree, .close-btn').forEach(b => {
        b.disabled = false;
        if (b.classList.contains('agree')) b.textContent = 'موافق';
      });
      if (modal) {
        modal.hide();
      }
      return;
    }

    const filename = Date.now().toString();
    files[currentIndex] = filename;

    const fnInput = document.querySelector('[name="fn"]');
    if (fnInput) {
      fnInput.value = isMultiple ? files.filter(Boolean).join(',') : filename;
    }

    const canvas = cropperInstance.getCroppedCanvas({
      width,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high'
    });
    const imgSrc = canvas.toDataURL('image/jpeg', 0.8);

    CroppedImageEvent(imgSrc);
    const base64 = imgSrc.split(',')[1];
    localStorage.setItem(`img${currentIndex}`, base64);
  }

  // === Inner Function: Show Cropped Image & Finalize ===
  function CroppedImageEvent(imgSrc) {
    isCropped = true;
    isUploadedBefore = true;
    const croppedImg = croppedImages[currentIndex];
    const resultDiv = imgResults[currentIndex];
    if (croppedImg) {
      croppedImg.src = imgSrc;
      croppedImg.classList.remove('hide');
    }
    if (resultDiv) {
      resultDiv.classList.remove('hide');
    }
    document.querySelectorAll('.agree').forEach(b => {
      b.textContent = 'موافق';
      b.disabled = false;
    });
    document.querySelectorAll('.close-btn').forEach(b => b.disabled = false);
    if (modal) {
      modal.hide();
    }
    if (cropper) {
      cropper.destroy();
      cropper = null;
    }
    // Smooth scroll to result
    if (croppedImg) {
      croppedImg.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    window.setTimeout(() => window.scrollBy(0, -120), 400);
  }
}
